import axios from 'axios';
import i18n from '../i18n';

const LANGUAGE_STORAGE_KEY = 'app-language';
const NETWORK_ERROR_TOAST_EVENT = 'app:network-error-toast';

let lastNetworkErrorToastAt = 0;

const notifyNetworkError = () => {
  const now = Date.now();
  if (now - lastNetworkErrorToastAt < 3000) {
    return;
  }
  lastNetworkErrorToastAt = now;

  window.dispatchEvent(
    new CustomEvent(NETWORK_ERROR_TOAST_EVENT, {
      detail: {
        message: i18n.t('common.networkError'),
      },
    })
  );
};

const buildNetworkError = (message: string) =>
  new axios.AxiosError(message, 'ERR_NETWORK');

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
});

api.interceptors.request.use((config) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    notifyNetworkError();
    return Promise.reject(buildNetworkError(i18n.t('common.networkError')));
  }

  try {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'zh-CN';
    config.headers['Accept-Language'] = lang;
  } catch (error) {
    console.error('Failed to get token/language from storage:', error);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response || error.code === 'ERR_NETWORK') {
      notifyNetworkError();
    }

    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or if the error comes from the login endpoint
      const url = String(error.config?.url ?? '');
      const isAuthEntryRequest = url.includes('/auth/login') || url.includes('/auth/renew');
      if (!isAuthEntryRequest) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('class-storage');
        } catch (e) {
          console.error('Failed to remove token:', e);
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
